<style scoped>
.modal-content {
    max-height: calc(100vh - 10rem);
    display: flex;
    flex-direction: column;
}

.video-player::v-deep.video-container {
    max-height: 70vh;
}

@screen sm {
    .modal-content {
        flex-direction: row;
        align-items: center;
    }
}
</style>

<template>
    <modal class="modal-6xl" type="custom" v-if="show">
        <div class="flex items-center text-sm sm:text-base">
            <div class="mr-auto">
                資訊播放後將會返回試吓啦
            </div>
            <a
                v-if="vdoAdvrtUrl"
                :href="vdoAdvrtUrl"
                @click.prevent="vdoAdvrtUrlClick"
                target="_blank"
                class="btn btn-primary px-2 sm:px-4"
            >
                {{ '更多資訊' }}
            </a>
            <button
                class="btn btn-white px-2 sm:px-4"
                :disabled="!canSkipVideo"
                @click="skipVideo"
            >
                略過資訊
            </button>
        </div>
        <div class="modal-content mt-2">
            <div class="flex-shrink-0 sm:flex-1 flex flex-col items-center">
                <videoPlayer
                    ref="videoPlayer"
                    class="video-player"
                    :allowPause="allowPause"
                    :autoPlay="autoPlay"
                    :coverImageUrl="coverImageBlobUrl"
                    :endImageUrl="endImageBlobUrl"
                    :videoSrc="videoBlobUrl"
                    :videoType="videoBlobContentType"
                    :canSkipAfterSeconds="videoAdvrt.skipVdoScndCnt"
                    @canSkip="canSkipVideo = true"
                    @skip="skipVideo"
                    @ended="videoEnded"
                    @error="videoError"
                    @videoClick="vdoAdvrtUrlClick"
                />
            </div>

            <div
                v-if="false"
                class="flex-1 flex flex-col overflow-y-auto sm:max-w-xs px-1"
            >
                <div class="flex-shrink-1 overflow-auto">
                    <p>
                        {{ 'Additional information Area' }}
                    </p>
                </div>
                <button class="mt-2 mx-auto btn btn-primary">
                    更多資訊
                </button>
            </div>
        </div>
    </modal>
</template>

<script>
import modal from './modal.vue';
import videoPlayer from './videoPlayer.vue';
import fetchLocal from '../utils/fetchLocal';
import ctx from '../utils/ctx';
import shuffle from 'lodash/shuffle';
import { marketingEventHandler } from '../utils/marketingDataProcessor.js';

const _login_vdoAdvrtTypeCode = 'LOGIN';
const _localStorage_key_vdo_ads_display_ind =
    'vdo_ads_login_should_display_immediately';
const _localStorage_key_vdo_ads_last_close_timestamp =
    'vdo_ads_login_close_timestamp_in_milli_second';
const _cooldown_milliSecond = 15 * 60 * 1000;

let _localStorage = {
    get lastCloseTimestampMilliSec() {
        const rawVal = window.localStorage.getItem(
            _localStorage_key_vdo_ads_last_close_timestamp
        );
        return parseInt(rawVal) || 0;
    },
    set lastCloseTimestampMilliSec(val) {
        window.localStorage.setItem(
            _localStorage_key_vdo_ads_last_close_timestamp,
            val
        );
    },
    get shouldDisplayVdoAdsImmediately() {
        const rawVal = window.localStorage.getItem(
            _localStorage_key_vdo_ads_display_ind
        );
        return rawVal === 'true';
    },
    set shouldDisplayVdoAdsImmediately(val) {
        window.localStorage.setItem(_localStorage_key_vdo_ads_display_ind, val);
    },
    updateShouldDisplayVdoAdsIndex: function() {
        const lastCloseTimestampMilliSec = this.lastCloseTimestampMilliSec;
        const currentMilliSec = new Date().getTime();
        const cooldownMilliSec = _cooldown_milliSecond;

        const newShouldDisplayVdoAdsIndex =
            !lastCloseTimestampMilliSec ||
            currentMilliSec - lastCloseTimestampMilliSec > cooldownMilliSec;

        this.shouldDisplayVdoAdsImmediately =
            this.shouldDisplayVdoAdsImmediately || newShouldDisplayVdoAdsIndex;
    }
};

export default {
    components: {
        modal,
        videoPlayer
    },
    props: {
        autoPlay: { type: Boolean, default: true },
        allowPause: { type: Boolean, default: false }
    },
    data() {
        return {
            show: false,
            canSkipVideo: false,
            videoAdvrt: undefined,
            videoBlobUrl: undefined,
            videoBlobContentType: undefined,
            coverImageBlobUrl: undefined,
            endImageBlobUrl: undefined,
            isMediaPrepared: false,
            isMediaPreparing: false
        };
    },
    watch: {
        $route: {
            handler(val, oldVal) {
                if (oldVal?.name === 'login') {
                    _localStorage.updateShouldDisplayVdoAdsIndex();
                }

                //this.showVideoAdvrt();
                this.prepareAdvrtMedia();
            },
            immediate: true
        }
    },
    destroyed() {
        this.revokeAllBlobUrls();
    },
    computed: {
        videoAdvrtList() {
            return (this.$store.state.videoAdvertisementList || []).filter(
                a => a.vdoAdvrtTypeCode === _login_vdoAdvrtTypeCode
            );
        },
        videoUrl() {
            return this.videoAdvrt?.vdoId
                ? ctx(`/videoAdvrt/video/${this.videoAdvrt.vdoId}`)
                : undefined;
        },
        coverImageUrl() {
            return this.videoAdvrt?.cvrImgId
                ? ctx(`/videoAdvrt/coverImage/${this.videoAdvrt.cvrImgId}`)
                : undefined;
        },
        endImageUrl() {
            return this.videoAdvrt?.endImgId
                ? ctx(`/videoAdvrt/endImage/${this.videoAdvrt.endImgId}`)
                : undefined;
        },
        vdoAdvrtUrl() {
            return this.videoAdvrt?.vdoAdvrtUrl;
        },
        shopListKey() {
            return this.$store.getters.shoppingList?.id;
        }
    },
    methods: {
        async prepareAdvrtMedia() {
            if (this.isMediaPreparing) {
                return;
            }

            // Randomly pick video advertisement
            this.videoAdvrt = shuffle(this.videoAdvrtList || [])[0];

            if (!this.videoAdvrt) {
                return;
            }
            if (!this.getShouldShowAds()) {
                return;
            }

            this.isMediaPreparing = true;
            this.isMediaPrepared = false;

            this.videoBlobUrl = undefined;
            this.videoBlobContentType = undefined;
            this.coverImageBlobUrl = undefined;
            this.endImageBlobUrl = undefined;

            let success = true;
            if (this.videoUrl) {
                success = await this.loadMediaResource(
                    this.videoUrl,
                    'videoBlobUrl',
                    'videoBlobContentType'
                );
            }
            if (this.coverImageUrl) {
                success = await this.loadMediaResource(
                    this.coverImageUrl,
                    'coverImageBlobUrl'
                );
            }
            if (this.endImageUrl) {
                success = await this.loadMediaResource(
                    this.endImageUrl,
                    'endImageBlobUrl'
                );
            }

            this.isMediaPreparing = false;
            this.isMediaPrepared = success;
            if (success) this.showVideoAdvrt();
        },
        getShouldShowAds() {
            return (
                this.$route.meta.showPopUpVideoAds === true &&
                _localStorage.shouldDisplayVdoAdsImmediately &&
                this.videoAdvrt
            );
        },
        showVideoAdvrt() {
            if (!this.isMediaPrepared) {
                return;
            }
            if (!this.getShouldShowAds()) {
                return;
            }

            this.show = true;
            marketingEventHandler.videoAdvertisementDisplay(
                this.shopListKey,
                'LOGIN',
                this.videoAdvrt.trckSlctKey
            );
            _localStorage.shouldDisplayVdoAdsImmediately = true;
        },
        closeVideoAdvrt() {
            this.show = false;
            _localStorage.shouldDisplayVdoAdsImmediately = false;
            _localStorage.lastCloseTimestampMilliSec = new Date().getTime();
            this.revokeAllBlobUrls();
        },
        vdoAdvrtUrlClick() {
            if (!this.vdoAdvrtUrl) {
                return;
            }

            this.closeVideoAdvrt();

            window.open(this.vdoAdvrtUrl, '_blank');
            marketingEventHandler.videoAdvertisementClick(
                this.shopListKey,
                'LOGIN',
                this.videoAdvrt.trckSlctKey
            );
        },
        skipVideo() {
            if (this.canSkipVideo) {
                this.closeVideoAdvrt();
            }
        },
        videoEnded() {
            this.canSkipVideo = true;
            if (this.videoAdvrt.vdoAdvrtAutoClsInd === 'Y' && !this.vdoAdvrtUrl)
                this.closeVideoAdvrt();
        },
        videoError() {
            this.closeVideoAdvrt();
        },
        async loadMediaResource(url, blobUrl, blobContentType) {
            if (this[blobUrl]) {
                window.URL.revokeObjectURL(this[blobUrl]);
                this[blobUrl] = undefined;
            }
            try {
                const response = await fetchLocal(url, 'blob');
                const contentType = (
                    (response.header && response.headers['content-type']) ||
                    response.type ||
                    'application/octet-stream'
                ).replace(';charset=utf-8', '');

                const blob =
                    response instanceof Blob
                        ? response
                        : new Blob([response.data], {
                              type: contentType
                          });

                if (blobContentType) this[blobContentType] = contentType;
                this[blobUrl] = window.URL.createObjectURL(blob);
                return true;
            } catch (e) {
                /* eslint-disable no-console */
                console.error('Cannot load resource', e, url);
                this[blobUrl] = undefined;
                return false;
            }
        },
        revokeAllBlobUrls() {
            const blobUrls = [
                'videoBlobUrl',
                'coverImageBlobUrl',
                'endImageBlobUrl'
            ];
            blobUrls.forEach(blobUrl => {
                window.URL.revokeObjectURL(this[blobUrl]);
                this[blobUrl] = undefined;
            });
        }
    }
};
</script>
